import { TaskModel } from '@bryntum/gantt';
import moment from 'moment';
import React, { useEffect, useMemo } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { useIntl } from 'react-intl';
import { ProjectPhase } from 'api/project';
import { Task } from 'api/tasks';
import { StatusIndicator } from 'components/molecules/status-indicator';
import { GanttType } from 'components/organisms/projects/project-plan/timeline/helpers';
import { useTranslation } from 'hooks/use-translation';
// eslint-disable-next-line import/no-internal-modules

export const useTemplateGanttStaticConfig = ({
  ganttRef,
}: {
  ganttRef: any;
}) => {
  const nameText = useTranslation('name');
  const startOnText = useTranslation('templates.starts_on');
  const durationText = useTranslation('templates.duration');
  const intl = useIntl();

  useEffect(() => {
    if (!ganttRef) {
      return;
    }
    ganttRef?.instance?.features.dependencies.tooltip.setConfig({
      getHtml: ({ activeTarget }) => {
        const dependency = ganttRef?.instance.dependencyStore.getById(
          activeTarget.getAttribute('depId'),
        );

        return `
          <table class="b-sch-dependency-tooltip">
            <tr>
                <td>${intl.formatMessage({ id: 'from' })}: </td>
                <td>${dependency?.fromEvent.name}</td>
            </tr>
            <tr>
                <td>${intl.formatMessage({ id: 'to' })}: </td>
                <td>${dependency?.toEvent.name}</td>
            </tr>
        </table>
        `;
      },
      destroy() {
        return null;
      },
    });
  }, [ganttRef, intl]);

  return useMemo(
    () => ({
      columns: [
        {
          type: 'name',
          width: 371,
          field: 'title',
          text: nameText,
          renderer: ({
            record,
            value,
          }: {
            record: TaskModel;
            value: string;
          }): string =>
            record.getData('type') === 'PROJECT_PHASE' ? `${value}:` : value,
        },
        {
          editor: false,
          text: '',
          field: 'status',
          width: 32,
          renderer: ({ record }) => {
            const recordData: Task | ProjectPhase = record.get('data');

            return (
              <StatusIndicator
                progress={0}
                status={{
                  label: 'To do',
                  group: 1,
                  color: 'blue',
                }}
                type={(recordData as Task).type}
              />
            );
          },
        },
        {
          type: 'startdate',
          text: startOnText,
          field: 'startDate',
          width: 93,
          renderer: ({ record }: { record: TaskModel }): string =>
            `${record.getData('startDay')}d`,
        },
        {
          type: 'customDuration',
          field: 'actualDuration',
          text: durationText,
          width: 60,
          renderer: ({ record }: { record: TaskModel }): string =>
            `${record.getData('actualDuration')}d`,
        },
      ],
      percentBarFeature: false,
      taskTooltipFeature: {
        template: (_data: {
          taskRecord: TaskModel;
          startText: string;
          endText: string;
        }): string => {
          return intl.formatMessage(
            { id: 'templates.gantt.bar.tooltip' },
            {
              startDay: _data.taskRecord.getData('startDay'),
              duration: _data.taskRecord.getData('actualDuration'),
            },
          );
        },
      },
      taskDragFeature: {
        tooltipTemplate({ dragData: { timeDiff }, taskRecord }) {
          const projectedStartDay = timeDiff / (24 * 60 * 60 * 1000);

          return intl.formatMessage(
            { id: 'templates.gantt.bar.tooltip.drag' },
            {
              startDay: taskRecord.getData('startDay') + projectedStartDay,
              duration: Math.round(taskRecord.getData('duration')),
            },
          );
        },
      },
      taskResizeFeature: {
        tooltipTemplate({ startDate, endDate, record: taskRecord }) {
          const projectedDuration = moment(endDate).diff(
            moment(startDate),
            'days',
            true,
          );

          return intl.formatMessage(
            { id: 'templates.gantt.bar.tooltip.drag' },
            {
              startDay: taskRecord.getData('startDay'),
              duration: Math.round(projectedDuration),
            },
          );
        },
      },

      minZoomLevel: 9,
      maxZoomLevel: 9,
      zoomKeepsOriginalTimespan: true,
      viewPreset: {
        defaultSpan: 15,
        timeResolution: {
          unit: 'day',
          increment: 1,
        },
        base: 'dayAndWeek',
        headers: [
          {
            unit: 'week',
            increment: 1,
          },
          {
            unit: 'day',
            increment: 1,
            renderer: (startDate) => {
              return moment(startDate)
                .startOf('day')
                .diff(moment().startOf('day'), 'day');
            },
          },
        ],
        tickWidth: 40,
      },

      labelsFeature: {
        right: {
          renderer: ({ taskRecord }) => {
            const { type } = taskRecord.get('data');
            const { title } = taskRecord;

            const isMilestone = type === GanttType.MilestoneBar;

            if (type === 'phase') {
              return `<label class='text-02'>${renderToStaticMarkup(
                title,
              )}</label>`;
            }

            return `
            <label class='text-02' style='display: flex; position: relative;'>
              ${
                isMilestone
                  ? '<label title="Milestone" class="gantt-type-milestone"></label>'
                  : ''
              }
              <span style='margin: 0 8px 0 2px;'>${renderToStaticMarkup(
                title,
              )}</span>
            </label>
          `;
          },
        },
      },
    }),
    [durationText, intl, nameText, startOnText],
  );
};
